import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CoachHome from './pages/CoachHome';
import Profile from './pages/Profile';
import Coaches from './pages/Coaches';
import Venues from './pages/Venues';
import Teams from './pages/Teams';
import Fixtures from './pages/Fixtures';
import Submissions from './pages/Submissions';
import Bills from './pages/Bills';
import Invoice from './pages/Invoice';

// A signed-in person is sent to the login screen if they are not signed in, and
// away from admin screens if they are not an admin. Plus one more gate:
//
// STILL ON SOMEBODY ELSE'S PASSWORD. must_change_password is set when an admin
// creates an account or resets a password, and cleared the moment the person
// chooses their own. While it is set they are held on the change-password form
// and nothing else, because a temporary password handed over on the phone or by
// text is the weakest thing in this portal and the gap between being given one
// and replacing it should be one screen long, not a season.
//
// `allowWhileLocked` is what the profile page is marked with so the gate does not
// redirect the very screen it is sending people to, which would loop for ever.
const PrivateRoute = ({ children, adminOnly, allowWhileLocked }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && !allowWhileLocked) return <Navigate to="/profile" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      {/* Both public, no token needed. /reset-password is NOT redirected away
          when somebody happens to be signed in: an admin who opens their own
          reset link on a laptop where they are already logged in should still be
          able to finish setting the new password. */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<PrivateRoute allowWhileLocked><Layout /></PrivateRoute>}>
        <Route index element={
          user?.must_change_password
            ? <Navigate to="/profile" replace />
            : (user?.role === 'admin' ? <Navigate to="/submissions" replace /> : <CoachHome />)
        } />
        {/* Both roles. It used to be coaches only, because the only thing on
            it was a photo and an admin has no photo. It now also holds the
            change-password form, which everybody needs — including the admin,
            who is otherwise stuck on whatever ADMIN_PASSWORD was set to in
            Coolify. One page for both was cheaper and less to maintain than a
            second admin-only screen holding one identical form. The page
            itself hides the photo card from an admin. */}
        <Route path="profile" element={<PrivateRoute allowWhileLocked><Profile /></PrivateRoute>} />
        <Route path="submissions" element={<PrivateRoute adminOnly><Submissions /></PrivateRoute>} />
        <Route path="bills" element={<PrivateRoute adminOnly><Bills /></PrivateRoute>} />
        {/* NOT adminOnly. A coach may open their own invoice and nobody
            else's, which the server enforces by answering 404 for anyone
            else's id. Gating the route to admins here would have hidden a
            coach's own invoice from them. */}
        <Route path="invoice/:id" element={<PrivateRoute><Invoice /></PrivateRoute>} />
        <Route path="coaches" element={<PrivateRoute adminOnly><Coaches /></PrivateRoute>} />
        <Route path="venues" element={<PrivateRoute adminOnly><Venues /></PrivateRoute>} />
        <Route path="teams" element={<PrivateRoute adminOnly><Teams /></PrivateRoute>} />
        <Route path="fixtures" element={<PrivateRoute adminOnly><Fixtures /></PrivateRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => (
  <BrowserRouter>
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  </BrowserRouter>
);

export default App;
